import { Location } from '@angular/common';
import { debounceTime, share } from 'rxjs/operators';
import { Observable, Subject, Subscription, combineLatest, ReplaySubject } from 'rxjs';
import { DialogDefinition } from './../../../services/overlay.service';
import { SapiEntity } from './../../../services/sapi.service';
import { PublicationService } from './../../../services/publication.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormArray, UntypedFormBuilder } from '@angular/forms';
import { User } from './../../../interfaces/user';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { ActivatedRoute } from '@angular/router';
import { bbDeepCopy } from '../../../shared/utils/bbDeepCopy.util';

export interface Role {
	id: string;
	name: string;
	label?: string;
	checked: boolean;
	permissions: string[];
	help?: string;
	disabled?: boolean;
}

@Component({
	selector: 'user-details',
	templateUrl: './user-details.component.html',
	styleUrls: ['./user-details.component.scss']
})

export class UserDetailsComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {
	user: User;
	activeTab: string;
	id: string;
	isNew: boolean;
	roles: Role[];
	roleNames: string[];
	extraRoles: Role[];
	extraRoleNames: string[];
	legacyRoles = true;
	legacyRolesInitial: boolean;
	repeat_password: string;
	passwordMatch: boolean;
	passwordCheck: RegExp;
	putRight: boolean;
	originalFormValues: any;
	nameChanged: boolean;

	subscriptions: Subscription;

	userSubject: ReplaySubject<User>;

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		private publicationService: PublicationService,
		private formBuilder: UntypedFormBuilder) {

		super(route, location);

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		this.userSubject = new ReplaySubject<User>(1);

		this.entityConfiguration = {
			name: 'user',
			parentPath: '/publication/users',
			basePath: '/publication/users/',
			defaults: {
				firstname: '',
				lastname: '',
				roles: [],
				status: 'active'
			} as User
		};

		this.formGroup = new UntypedFormGroup({
			firstname: new UntypedFormControl('', { validators: Validators.required, updateOn: 'change' }),
			lastname: new UntypedFormControl('', { validators: Validators.required, updateOn: 'change' }),
			name: new UntypedFormControl('', Validators.required),
			email: new UntypedFormControl('', [Validators.required, Validators.email]),
			roles: new UntypedFormArray([])
		},
		{ updateOn: 'blur' }
		);

		this.passwordCheck = new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}'); // at least eight characters, containing at least one lowercase, one uppercase, one digit
		this.roleNames = ['ovp-analyst', 'ovp-editor', 'ovp-publisher', 'ovp-administrator'];
	}

	ngOnInit() {
		super.ngOnInit();

		if (this.isNew) {
			this.formGroup.addControl('password', new UntypedFormControl(''));
			this.skipFormGroupSave.push('repeat_password');
			this.formGroup.addControl('repeat_password', new UntypedFormControl());
		}

		this.user = { ...this.entity } as User;

		this.subscriptions.add(
			this.formGroup.valueChanges.pipe(
				debounceTime(500))
				.subscribe(newValue => this.checkValues(false, newValue))
		);

		if (this.isNew) {
			this.userSubject.next(this.entityConfiguration.defaults);
		}

		this.subscriptions.add(
			combineLatest([this.publicationService.getRoles(), this.userSubject]).subscribe(([roles, apikey]: any) => {
				if (this.isNew) {
					if (roles) {
						this.extraRoleNames = roles.filter((role: Role) => role.name && role.name.match(/ovp-\w+-extra/)).map((role: Role) => role.name);
					}
					this.extraRoleNames.sort();
					this.legacyRoles = this.publicationService.hasPublicationField('useLegacyRoles');
					this.legacyRolesInitial = this.legacyRoles;
					this.getRoles();
				} else {
					if (roles) {
						this.extraRoleNames = roles.filter((role: Role) => role.name && role.name.match(/ovp-\w+-extra/)).map((role: Role) => role.name);
					}
					this.extraRoleNames.sort();
					this.legacyRoles = this.hasLegacyRoles(apikey.roles);
					this.legacyRolesInitial = this.legacyRoles;
					this.getRoles();
					this.formGroup.markAsPristine();
				}
			})
		);
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	addRolesUsingNames(roleNames: string[], userRoles: Role[], roles: Role[]): Role[] {
		const rolesForUser: Role[] = [];
		roleNames.forEach((roleName: string) => {
			userRoles.filter((role: Role) => role.name === roleName).map((role: Role) => {
				if (this.user.roles.filter((internalRole: Role) => internalRole.name === role.name).length) {
					rolesForUser.push(role);
				}
				role.help = 'HELP.' + role.label.replace(' ', '').toUpperCase() + '_ROLE';
				roles.push({ ...role });
			});
		});
		return rolesForUser;
	}

	hasLegacyRoles(roles: Role[]) {
		let hasLegacyRoles = true;
		roles.forEach((role: Role) => {
			if (this.roleNames.includes(role.name)) {
				hasLegacyRoles = false;
			}
		});
		return hasLegacyRoles;
	}

	getRoles() {
		this.publicationService.getRoles().subscribe((roles: Role[]) => {
			if (!this.legacyRoles) {
				this.legacyRoles = false;
				this.roles = [];
				this.extraRoles = [];
				let userRoles: Role[] = this.addRolesUsingNames(this.roleNames, roles, this.roles);
				this.roleChanged(userRoles[userRoles.length - 1], true);
				userRoles = this.addRolesUsingNames(this.extraRoleNames, roles, this.extraRoles);
				userRoles.forEach((role: Role) => {
					this.roleChanged(role, true);
				});

			} else {
				this.legacyRoles = true;
				this.extraRoles = null;
				roles = roles.filter((role: Role) => !role.name.startsWith('ovp-'));
				this.roles = bbDeepCopy(roles).sort((a: any, b: any) => {
					const textA = a.name.toUpperCase();
					const textB = b.name.toUpperCase();
					return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
				});
				this.checkActiveRoles();
			}
		});
	}

	switchPermission() {
		this.legacyRoles = !this.legacyRoles;
		this.removeRolesFromFormControl();
		if (this.legacyRoles) {
			this.extraRoleNames.forEach((roleName: string) => {
				this.removeRolesFromFormControl(roleName);
			});
		}
		this.getRoles();
		this.formGroup.markAsDirty();
	}

	getLabel() {
		const user = this.entity as User;
		return user ? (user.firstname + ' ' + user.lastname) : '';
	}

	cancel(): Subject<SapiEntity> {
		const cancelSubject = super.cancel();
		cancelSubject.subscribe(u => {
			this.formGroup.reset(u);
			this.getRoles();

			if (this.legacyRoles) {
				this.checkActiveRoles();
			}
		});
		return cancelSubject;
	}

	checkActiveRoles() {
		const roles = this.formGroup.get('roles') as UntypedFormArray;

		if (roles && roles.controls) {
			this.roles.forEach((role: Role) => {
				const index = roles.controls.findIndex((formRole: UntypedFormControl) => formRole.value.id === role.id);
				role.checked = (index > -1);
			});
		}
	}

	delete(dialogContents?: string): Subject<void> {
		let numPubs = 1;
		if (this.user && this.user.publications) {
			numPubs = this.user.publications.length;
		}

		dialogContents = this.translate.instant('DELETE_ENTITY_CONFIRM', {
			entity: this.translate.instant('USER').toLowerCase()
		}) +
			((numPubs < 2) ? '' : ' ' + this.translate.instant(
				(numPubs === 2 ? 'USER_IS_AUTHENTICATED_TO_X_OTHER_PUBLICATION' : 'USER_IS_AUTHENTICATED_TO_X_OTHER_PUBLICATIONS'), {
					x: (numPubs - 1)
				}));
		return super.delete(dialogContents);
	}

	save(force: boolean = false): Observable<void> {
		this.entity = { ...this.user } as SapiEntity;

		this.checkValues(true);
		const saveSubject = super.save(force, null, false);

		saveSubject.subscribe(() => {
			this.user = { ...this.entity } as User;
			this.legacyRolesInitial = this.legacyRoles;
		});

		return saveSubject;
	}

	load(id: string, duplicating?: boolean): Observable<SapiEntity> {
		const loadSubject = super.load(id, duplicating);

		loadSubject.subscribe((entity) => {
			const user = entity as User;
			if (duplicating) {
				delete user.firstname;
				delete user.lastname;
				delete user.name;
				if (user.email && user.email.indexOf('@') !== -1) {
					user.email = '@' + user.email.split('@')[1];
				} else {
					delete user.email;
				}
			}
			this.user = { ...user } as User;

			this.formGroup.controls.firstname.setValue(this.user.firstname);
			this.formGroup.controls.lastname.setValue(this.user.lastname);
			this.formGroup.controls.name.setValue(this.user.name);
			this.formGroup.controls.email.setValue(this.user.email);

			const roles = this.formGroup.controls.roles as UntypedFormArray;

			this.user.roles.forEach((role: Role) => {
				const formRole = new UntypedFormGroup({
					id: new UntypedFormControl(role.id),
					name: new UntypedFormControl(role.name),
					permissions: this.formBuilder.array(role.permissions)
				});
				roles.push(formRole);
			});

			this.userSubject.next(user);

			this.formGroup.markAsPristine();
			this.formGroup.markAsUntouched();
		});

		return loadSubject;
	}

	checkValues(whileSaving: boolean, newValue: any = null) {
		if (this.formGroup && this.formGroup.controls) {
			// password equal:
			if (this.formGroup.controls.password && this.formGroup.controls.repeat_password &&
				!(this.formGroup.controls.password.pristine || this.formGroup.controls.repeat_password.pristine) &&
				(this.formGroup.controls.password.value && this.formGroup.controls.repeat_password.value)) {

				if (this.formGroup.controls.repeat_password.value !== this.formGroup.controls.password.value) {
					this.formGroup.controls.repeat_password.setErrors({ incorrect: this.translate.instant('PASSWORDS_DONT_MATCH') });
				}

				// require passwords to either pass the test or be at least 16 chars long
				if (!this.passwordCheck.test(this.formGroup.controls.password.value) &&
					this.formGroup.controls.password.value.length < 16) {
					this.formGroup.controls.repeat_password.setErrors({ incorrect: this.translate.instant('CHANGE_PASSWORD_REQUIREMENTS') });
				}
			}

			// at least one role:
			if (this.formGroup.controls.roles) {
				if (!this.formGroup.controls.roles || this.formGroup.controls.roles.value.length === 0) {
					this.formGroup.controls.roles.setErrors({ incorrect: this.translate.instant('SELECT_AT_LEAST_ONE_ROLE') });
				}
			}
		}
	}

	resetPassword() {
		if (this.user && this.user.id) {
			const dialog: DialogDefinition = {
				icon: 'refresh',
				title: this.translate.instant('RESETTING_PASSWORD'),
				contents: this.translate.instant('RESET_PASSWORD_CONFIRM', { email: this.user.email }),
				onConfirm: () => {

					const resetPasswordObservable = this.sapiService.getUrl(`/user/${this.user.id}/resetPassword`)
						.pipe(
							share()
						);
					resetPasswordObservable.subscribe(() => {
						this.overlayService.createToast(this.translate.instant('SUCCESSFULLY_RESET_PASSWORD_FOR', { id: this.getLabel() }), 'check');
					});

					return resetPasswordObservable;
				}
			};

			this.overlayService.createDialog(dialog);
		}
	}

	setUserName() {
		if (this.isNew) {
			const firstName = this.formGroup.controls.firstname.value;
			const lastName = this.formGroup.controls.lastname.value;
			let name = firstName && firstName.charAt(0) ? firstName.charAt(0).toLowerCase() : '';
			name = (name && name.charAt(0) ? name + '.' : '') +
				(lastName && lastName.length > 0 ? lastName.toLowerCase().replace(/[^\w-]/g, '') : '');
			this.formGroup.get('name').patchValue(name);
		}
	}

	removeRolesFromFormControl(roleName?: string) {
		const roles: UntypedFormArray = this.formGroup.controls.roles as UntypedFormArray;
		for (let i = roles.length - 1; i >= 0; i--) {
			let roleFormControl = null;
			if (roleName) {
				if (roles.value) {
					roleFormControl = roles.value[i];
				}
				if (roleFormControl && roleFormControl.name === roleName) {
					roles.removeAt(i);
					return;
				}
			} else {
				if (!this.extraRoleNames.includes(roleName)) {
					roles.removeAt(i);
				}
			}
		}
	}

	addRole(role: Role) {
		const roles: UntypedFormArray = this.formGroup.controls.roles as UntypedFormArray;
		const fr = roles.value && roles.value.find((r) => r.name === role.name);
		if (!fr) {
			const formRole = new UntypedFormGroup({
				id: new UntypedFormControl(role.id),
				name: new UntypedFormControl(role.name),
				permissions: this.formBuilder.array(role.permissions)
			});
			roles.push(formRole);
		}
	}

	roleChanged(role: Role, enabled: boolean) {
		if (!role) {
			return;
		}
		const roles: UntypedFormArray = this.formGroup.controls.roles as UntypedFormArray;
		let roleIndex = -1;
		let nameIndex = -1;
		let roleName = role.name;

		if (!this.legacyRoles) {
			if ((nameIndex = this.roleNames.indexOf(roleName)) > -1) {
				this.removeRolesFromFormControl(roleName);
				if (!enabled && nameIndex > 0) {
					nameIndex = nameIndex - 1;
					roleName = this.roleNames[nameIndex];
				}
				let index = 0;
				while (index <= nameIndex) {
					const internalRoleName = this.roleNames[index];
					this.roles.filter((internalRole: Role) => internalRole.name === internalRoleName).map((internalRole: Role) => {
						if (enabled) {
							internalRole.checked = true;
						}
						if (internalRole.name === roleName) {
							internalRole.disabled = false;
							this.addRole(internalRole);
						} else {
							internalRole.disabled = true;
						}
					});
					index++;
				}
			} else {
				if (enabled) {
					this.addRole(role);
				} else {
					this.removeRolesFromFormControl(roleName);
				}
				this.extraRoles.forEach((internalRole: Role) => {
					if (internalRole.name === role.name) {
						internalRole.checked = enabled;
					}
				});
			}
		} else {
			roles.controls.forEach((roleFormControl: UntypedFormControl, index) => {
				if (roleFormControl.value && roleFormControl.value.id === role.id) {
					roleIndex = index;
				}
			});
			if (enabled) {
				if (roleIndex === -1) {
					const formRole = new UntypedFormGroup({
						id: new UntypedFormControl(role.id),
						name: new UntypedFormControl(role.name),
						permissions: this.formBuilder.array(role.permissions)
					});
					roles.push(formRole);
				}
			} else if (roleIndex > -1 && roleIndex < roles.length) {
				roles.removeAt(roleIndex);
			}
		}
		this.formGroup.markAsDirty();
	}
}
